import React from "react";
import { CalendarCheck, ArrowUpRight, Clock, Sparkles, Gift } from "lucide-react";
import { COACHING_URL } from "../lib/constants";
import { trackEvent } from "../lib/analytics";

export const DiscoveryCall = () => {
    return (
        <section
            id="coaching"
            data-testid="coaching-section"
            className="relative py-20 md:py-28"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="grid grid-cols-12 gap-8 items-stretch">
                    {/* Left: invitation card */}
                    <div className="col-span-12 lg:col-span-7 relative overflow-hidden rounded-[36px] bg-white border border-border p-8 md:p-12">
                        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-gold/10 blur-3xl" aria-hidden />
                        <div className="absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-lilac-200/40 blur-3xl" aria-hidden />

                        <div className="relative">
                            <div className="eyebrow text-gold-deep flex items-center gap-2 mb-5">
                                <CalendarCheck className="w-3.5 h-3.5" /> 1:1 Coaching with Coach Melissa
                            </div>

                            <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-lilac-900">
                                Book a 15-minute
                                <br />
                                <em className="italic gold-text">Discovery Call.</em>
                            </h2>

                            {/* First-call-free pill */}
                            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold/12 border border-gold/30 px-4 py-2">
                                <Gift className="w-4 h-4 text-gold-deep" />
                                <span className="text-sm font-medium text-lilac-900">
                                    First call <em className="not-italic" style={{ color: "#B8902A" }}>free</em>
                                </span>
                            </div>

                            <p className="mt-7 text-lilac-700 leading-relaxed max-w-lg">
                                If you've felt stuck in cycles you didn't choose — let's talk. A 15-minute
                                conversation, no pressure. We'll explore where you are, where God is calling
                                you, and whether 1:1 coaching is the next right step for you.
                            </p>

                            <ul className="mt-7 space-y-2.5 text-sm text-lilac-700">
                                {[
                                    "A safe, judgment-free space to share your heart",
                                    "Clarity on the cycles you're ready to break",
                                    "A real next step — even if we don't keep working together",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-2.5">
                                        <span
                                            className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0"
                                            aria-hidden
                                        />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-9 flex flex-wrap items-center gap-4">
                                <a
                                    href={COACHING_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackEvent("discovery_call_clicked")}
                                    data-testid="coaching-cta-link"
                                    className="group inline-flex items-center gap-2 rounded-full h-12 pl-6 pr-5 bg-lilac-900 hover:bg-lilac-700 text-white text-sm font-medium shadow-[0_12px_32px_rgba(43,29,53,0.22)] transition-colors"
                                >
                                    Book Your Free Call
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                                <span className="text-xs text-lilac-700 inline-flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" /> Schedule via{" "}
                                    <span className="text-lilac-900">walkwithpurposeco.site</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right: stat / promise tile */}
                    <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4">
                        <div className="col-span-2 rounded-[36px] bg-gradient-to-br from-lilac-100 via-[#FAF5FC] to-white border border-lilac-200 p-7 md:p-9 flex flex-col justify-between">
                            <div className="eyebrow text-gold-deep flex items-center gap-2">
                                <Sparkles className="w-3.5 h-3.5" /> What's included
                            </div>
                            <div className="font-display text-3xl md:text-4xl text-lilac-900 leading-tight mt-6">
                                15 minutes.
                                <br />
                                <span className="italic gold-text">Zero pressure.</span>
                            </div>
                            <p className="mt-4 text-sm text-lilac-700 leading-relaxed">
                                Pure space to be heard, prayed for, and gently coached toward what's next —
                                whether that's working together or simply walking lighter.
                            </p>
                        </div>

                        <div className="rounded-3xl bg-white border border-border p-5 flex flex-col justify-between">
                            <div className="eyebrow text-lilac-700">Format</div>
                            <div className="font-display text-2xl text-lilac-900 mt-3 leading-tight">
                                Video or phone
                            </div>
                            <p className="text-xs text-lilac-700 mt-2">Whatever feels safe.</p>
                        </div>

                        <div className="rounded-3xl bg-white border border-border p-5 flex flex-col justify-between">
                            <div className="eyebrow text-lilac-700">Investment</div>
                            <div className="font-display text-2xl gold-text mt-3 leading-tight">
                                Free
                            </div>
                            <p className="text-xs text-lilac-700 mt-2">First call only.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
